import QueryString from "qs";
import {useState, useEffect} from "react";
import {v4 as uuidv4} from "uuid";
import {cdxRequest} from "../../lib/request";
import {useLocalStorageState} from "../../lib/storage";

const useVisitorId = (root, projectId) => {
  const [newSession, setNewSession] = useState(null);
  const userId = root.loggedInUser && root.loggedInUser.$meta.get("id", null);
  // only get key if user and project is loaded
  const key = (root.loggedInUser && !userId) || !projectId ? null : `visitorId-${projectId}`;
  const [val, setVal] = useLocalStorageState(key);
  useEffect(() => {
    if (!key) return;
    if (!val) {
      setVal({
        id: userId || uuidv4(),
        createdAt: new Date().toISOString(),
        lastSessionStarted: new Date().toISOString(),
      });
      setNewSession(true);
    } else if (newSession === null) {
      const lastSessionTs = new Date(val.lastSessionStarted).getTime();
      const age = new Date().getTime() - lastSessionTs;
      if (age > 2 * 3600 * 1000) {
        setNewSession(true);
        setVal((prev) => ({...prev, lastSessionStarted: new Date().toISOString()}));
      } else {
        setNewSession(false);
      }
    }
  }, [val, key, userId, setVal, newSession]);
  return val ? {visitorId: userId || val.id, newSession} : {visitorId: null, newSession: null};
};

const getReferrer = () => {
  try {
    return document.referrer || null;
  } catch (e) {
    return null;
  }
};

const getUtmParams = () => {
  try {
    const params = QueryString.parse(window.location.search.slice(1));
    const entries = [];
    if (params.utm_medium) entries.push(["medium", params.utm_medium]);
    return entries.length > 0 ? Object.fromEntries(entries) : null;
  } catch {
    return null;
  }
};

const useOpenTracker = ({root, projectId}) => {
  const [hasInteracted, setHasInteracted] = useState(false);
  const [hasWaited, setHasWaited] = useState(false);
  const [hasBeenSent, setHasBeenSent] = useState(false);
  const [utmParams] = useState(getUtmParams);

  const {visitorId, newSession} = useVisitorId(root, projectId);

  useEffect(() => {
    const handler = () => {
      setHasInteracted(true);
      removeHandler();
    };
    let removeHandler = () => {
      document.removeEventListener("pointermove", handler);
      document.removeEventListener("mousemove", handler);
      removeHandler = null;
    };
    document.addEventListener("pointermove", handler);
    document.addEventListener("mousemove", handler);
    return () => {
      if (removeHandler) removeHandler();
    };
  }, []);

  useEffect(() => {
    setHasWaited(false);
    let id = setTimeout(() => {
      setHasWaited(true);
      id = null;
    }, 5000);
    return () => {
      if (id) clearTimeout(id);
    };
  }, [visitorId, projectId]);

  useEffect(() => {
    if (!hasBeenSent && hasWaited && hasInteracted && newSession && visitorId) {
      cdxRequest({
        path: "/events/visit",
        method: "POST",
        data: {visitorId, projectId, referrer: getReferrer(), utmParams},
      });
      setHasBeenSent(true);
    }
  }, [hasWaited, hasInteracted, visitorId, projectId, hasBeenSent, newSession, utmParams]);
};

export default useOpenTracker;
